import React, { useCallback } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  Alert,
  Collapse,
  IconButton,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  Groups,
  Settings,
  CheckCircle,
  Google,
  PlayCircleOutline,
  InfoOutlined,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";

import CustomInput from "../../components/CustomInput";
import InstructionStep from "../../components/InstructionStep";
import NightShiftBadge from "../../components/NightShiftBadge";
import { getLoomEmbedUrl } from "../../utils/calendarUtils";
import { calendarProviders, providerInstructions } from "../../utils/providerConfig";

const TeamScheduling = ({
  domainId,
  singleProvider,
  googleStatus,
  teamScheduling,
  snackbar,
  setSnackbar,
  confirmDialog,
  setConfirmDialog,
  showVideo,
  setShowVideo,
  currentVideoUrl,
  setCurrentVideoUrl,
  showSettings,
  setShowSettings,
  savingSettings,
  setSavingSettings,
  teamManagementModalOpen,
  setTeamManagementModalOpen,
  isSavingTeamProvider,
  setIsSavingTeamProvider,
  isRemovingAccount,
  isMobile,
  LOOM_SHARE_URLS,
  handleConnectGoogle,
  handleSaveTeamSchedulingProvider,
  handleRemoveGoogleAccount,
  handleEnableTeamScheduling
}) => {

  const isTeamSchedulingActive = singleProvider.existingProvider === "team-scheduling";

  const handleSaveSettings = useCallback(async () => {
    setSavingSettings(true);
    try {
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/config`,
        teamScheduling.schedulingSettings,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Settings saved successfully!",
        });
        setShowSettings(false);
        teamScheduling.fetchTeamSchedulingStatus();
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (err) {
      console.error("Failed to save settings:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to save settings. Please try again.",
      });
    } finally {
      setSavingSettings(false);
    }
  }, [teamScheduling, domainId, setSnackbar, setShowSettings]);

  const handleWatchVideo = useCallback(() => {
    const embedUrl = getLoomEmbedUrl(LOOM_SHARE_URLS['team-scheduling']);
    if (embedUrl) {
      setCurrentVideoUrl(embedUrl);
      setShowVideo(true);
      
      if (isMobile) {
        setTimeout(() => {
          document.querySelector('.video-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [LOOM_SHARE_URLS, setCurrentVideoUrl, setShowVideo, isMobile]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Paper sx={{ 
          p: { xs: 2, md: 3 }, 
          borderRadius: '12px', 
          backgroundColor: '#f8fafa',
          border: '1px solid #e0f0f0'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: '#219297',
                }}
              >
                <Groups />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  Team Scheduling
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                  Round-robin scheduling with multiple team members
                </Typography>
              </Box>
            </Box>
            
            {isTeamSchedulingActive && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    backgroundColor: '#b41d1dff',
                    color: '#ffffff',
                    "&:hover": {
                      backgroundColor: "#9c091fff",
                      borderColor: "#b71c1c",
                      color: "#ffffff",
                    },
                  }}
                >
                  Disconnect
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ 
            mb: 3, 
            p: { xs: 2, md: 2.5 }, 
            backgroundColor: '#f8fafa', 
            borderRadius: '12px',
            border: '1px solid #e0f0f0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg"
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#fff',
                    color: '#219297',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <Google />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                    Google Calendar Connection
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                    Required for team scheduling
                  </Typography>
                </Box>
              </Box>
              {googleStatus.isLoading ? (
                <CircularProgress size={24} sx={{ color: '#219297' }} />
              ) : (
                <Chip
                  label={googleStatus.hasGoogleCredentials ? "Calendar connected" : "Not Connected"}
                  size="small"
                  sx={{
                    backgroundColor: googleStatus.hasGoogleCredentials ? "#2e7d32" : "#757575",
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {googleStatus.hasGoogleCredentials ? (
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#e8f5e9', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ color: '#2e7d32' }} />
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                    Google Calendar is connected
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setConfirmDialog({ 
                    open: true, 
                    action: "remove-account",
                    data: { provider: "google-calendar" }
                  })}
                  disabled={isRemovingAccount}
                  sx={{
                    borderColor: "#d32f2f",
                    color: "#d32f2f",
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#ffebee",
                      borderColor: "#b71c1c",
                    },
                  }}
                >
                  Remove Account
                </Button>
              </Box>
            ) : (
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography variant="body2" sx={{ color: '#e65100', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                  Google Calendar is not connected
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleConnectGoogle}
                  disabled={googleStatus.isLoading}
                  sx={{
                    backgroundColor: "#219297",
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    padding: '6px 16px',
                    color: "#ffffff",
                    '&:hover': {
                      backgroundColor: "#1a767a",
                      color: "#ffffff",
                    },
                  }}
                >
                  {googleStatus.isLoading ? "Connecting..." : "Connect Google Calendar"}
                </Button>
              </Box>
            )}
          </Box>

          {googleStatus.hasGoogleCredentials && (
            <Box>
              {teamScheduling.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <CircularProgress sx={{ color: "#219297", mr: 2 }} />
                  <Typography sx={{ color: "#666" }}>Loading team scheduling data...</Typography>
                </Box>
              ) : teamScheduling.teamSchedulingStatus ? (
                <Box>
                  <Box sx={{ 
                    mb: 3, 
                    p: 2.5, 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '8px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#2e7d32' }}>
                        Team Scheduling is {teamScheduling.teamSchedulingStatus?.can_accept_bookings ? 'Active' : 'Configured'}
                      </Typography>
                      {teamScheduling.teamMembers.some(m => m.is_night_shift) && (
                        <Chip
                          label={`${teamScheduling.teamMembers.filter(m => m.is_night_shift).length} Night Shifts`}
                          size="small"
                          sx={{ 
                            backgroundColor: '#4527a0',
                            color: '#fff',
                            fontSize: '0.65rem'
                          }}
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
                      gap: 2,
                      mb: 2 
                    }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Team Members
                        </Typography>
                        <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                          {teamScheduling.teamMembers?.length || 0} members configured
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Slot Duration
                        </Typography>
                        <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                          {teamScheduling.schedulingSettings.slot_duration_minutes} minutes
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                          Booking Window
                        </Typography>
                        <Typography variant="body2" fontWeight="500" sx={{ color: '#219297' }}>
                          {teamScheduling.schedulingSettings.max_booking_days} days
                        </Typography>
                      </Box>
                    </Box>
                    
                    {!isTeamSchedulingActive && (
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={handleSaveTeamSchedulingProvider}
                        disabled={isSavingTeamProvider}
                        sx={{
                          backgroundColor: "#219297",
                          fontWeight: 600,
                          textTransform: "none",
                          color: "#fff",
                          borderRadius: "8px",
                          padding: "10px 16px",
                          "&:hover": {
                            backgroundColor: "#1a767a",
                          },
                        }}
                      >
                        {isSavingTeamProvider ? "Enabling..." : "Enable Team Scheduling"}
                      </Button>
                    )}
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => setTeamManagementModalOpen(true)}
                      startIcon={<Groups />}
                      sx={{
                        backgroundColor: "#219297",
                        fontWeight: 600,
                        textTransform: "none",
                        color: "#fff",
                        borderRadius: "8px",
                        padding: "10px 16px",
                      }}
                    >
                      Manage Team Members
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setShowSettings(!showSettings)}
                      startIcon={<Settings />}
                      sx={{
                        borderColor: "#219297",
                        color: "#219297",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: "10px 16px",
                        "&:hover": {
                          borderColor: "#1a767a",
                          color: "#1a767a",
                        },
                      }}
                    >
                      {showSettings ? "Hide Settings" : "Show Settings"}
                    </Button>
                  </Box>
                  
                  <Collapse in={showSettings}>
                    <Box sx={{ mt: 3, p: 2.5, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                      <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#219297', mb: 2 }}>
                        Scheduling Settings
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Slot Duration (minutes)</InputLabel>
                            <Select
                              value={teamScheduling.schedulingSettings.slot_duration_minutes}
                              onChange={(e) => teamScheduling.setSchedulingSettings({
                                ...teamScheduling.schedulingSettings,
                                slot_duration_minutes: e.target.value
                              })}
                              label="Slot Duration (minutes)"
                            >
                              <MenuItem value={15}>15 minutes</MenuItem>
                              <MenuItem value={30}>30 minutes</MenuItem>
                              <MenuItem value={45}>45 minutes</MenuItem>
                              <MenuItem value={60}>60 minutes</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Max Booking Days</InputLabel>
                            <Select
                              value={teamScheduling.schedulingSettings.max_booking_days}
                              onChange={(e) => teamScheduling.setSchedulingSettings({
                                ...teamScheduling.schedulingSettings,
                                max_booking_days: e.target.value
                              })}
                              label="Max Booking Days"
                            >
                              <MenuItem value={30}>30 days</MenuItem>
                              <MenuItem value={60}>60 days</MenuItem>
                              <MenuItem value={90}>90 days</MenuItem>
                              <MenuItem value={180}>180 days</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          onClick={() => setShowSettings(false)}
                          sx={{
                            color: "#666",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "8px 16px",
                          }}
                        >
                          Cancel
                        </Button>
                        
                        <Button
                          variant="contained"
                          onClick={handleSaveSettings}
                          disabled={savingSettings}
                          sx={{
                            backgroundColor: "#219297",
                            fontWeight: 600,
                            textTransform: "none",
                            color: "#fff",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            "&:hover": {
                              backgroundColor: "#1a767a",
                            },
                          }}
                        >
                          {savingSettings ? "Saving..." : "Save Settings"}
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ) : (
                <Box sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '8px',
                  border: '1px dashed #e0e0e0'
                }}>
                  <Groups sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" fontWeight="600" sx={{ color: '#666', mb: 1 }}>
                    Team Scheduling Not Configured
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                    Set up team scheduling to enable round-robin booking with multiple team members
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleEnableTeamScheduling}
                    disabled={!googleStatus.hasGoogleCredentials || teamScheduling.isLoading}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: "12px 24px",
                      fontSize: '0.95rem',
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#999",
                      },
                    }}
                  >
                    {!googleStatus.hasGoogleCredentials ? "Connect Google Calendar First" : "Setup Team Scheduling"}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={4}>
        <Paper sx={{ 
          p: { xs: 2, md: 2.5 }, 
          borderRadius: '12px', 
          backgroundColor: '#f8fafa',
          border: '1px solid #e0f0f0',
          height: '100%'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: '#21929715',
                color: '#219297',
              }}
            >
              <InfoOutlined />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                How Team Scheduling Works
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                Round-robin scheduling with multiple team members
              </Typography>
            </Box>
          </Box>
          
          <Box className="video-section" sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 1.5 
            }}>
              {LOOM_SHARE_URLS['team-scheduling'] && (
                <Button
                  startIcon={<PlayCircleOutline />}
                  onClick={handleWatchVideo}
                  size="small"
                  sx={{
                    color: '#219297',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#e0f7fa',
                    },
                  }}
                >
                  Watch Guide
                </Button>
              )}
            </Box>
            
            <Collapse in={showVideo}>
              <Box sx={{ 
                position: 'relative', 
                width: '100%', 
                pt: '56.25%',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#000',
                mb: 1.5
              }}>
                {currentVideoUrl && (
                  <iframe
                    src={currentVideoUrl}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Team Scheduling Setup Guide"
                  />
                )}
              </Box>
            </Collapse>
          </Box>
          
          <Box sx={{ 
            backgroundColor: '#fff', 
            borderRadius: '8px', 
            p: 2.5,
            border: '1px solid #e0f0f0'
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2, 
                color: '#219297',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <InfoOutlined fontSize="small" />
              Key Features
            </Typography>
            
            {providerInstructions['team-scheduling']?.map((step, index) => (
              <InstructionStep
                key={index}
                number={index + 1}
                title={step.title}
              >
                {step.content}
              </InstructionStep>
            ))}
            
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              backgroundColor: '#e3f2fd', 
              borderRadius: '6px',
              border: '1px solid #bbdefb'
            }}>
              <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Night Shift Support:
              </Typography>
              <Typography variant="caption" sx={{ color: '#0d47a1', display: 'block', mb: 0.5 }}>
                • Supports night shifts (18:00 → 03:00)
              </Typography>
              <Typography variant="caption" sx={{ color: '#0d47a1', display: 'block', mb: 0.5 }}>
                • Working hours can span across midnight
              </Typography>
              <Typography variant="caption" sx={{ color: '#0d47a1', display: 'block' }}>
                • Each member can have individual schedule
              </Typography>
            </Box>
            
            <Box sx={{ 
              mt: 2, 
              p: 1.5, 
              backgroundColor: '#e8f5e9', 
              borderRadius: '6px',
              border: '1px solid #c8e6c9'
            }}>
              <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Benefits:
              </Typography>
              <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block', mb: 0.5 }}>
                • Automatically distributes meetings among team members
              </Typography>
              <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block', mb: 0.5 }}>
                • Prevents double-booking across team calendars
              </Typography>
              <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block', mb: 0.5 }}>
                • Respects individual working hours and availability
              </Typography>
              <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block' }}>
                • Priority-based assignment for team members
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default TeamScheduling;